import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import SettingsCard from './SettingsCard';

/**
 * Inställningar → Automatisering, "Schemalagt": everything LedgerLoop runs
 * on a schedule for the company (GET /:companyId/schedules). Read-only — a
 * schedule is switched on, off or retimed on the settings page of the area
 * that owns it, which each row links to.
 *
 * Renders nothing while there is nothing scheduled, so a company that uses
 * no scheduled routine never sees an empty card.
 */

const formatWhen = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleString('sv-SE', {
    timeZone: 'Europe/Stockholm', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const lastResult = (run) => {
  if (!run) return { text: 'Har inte körts än', color: '#6b7280' };
  const when = formatWhen(run.finishedAt || run.scheduledFor);
  if (run.status === 'succeeded') return { text: `Genomförd ${when}`, color: '#6b7280' };
  if (run.status === 'failed') return { text: `Misslyckades ${when}`, color: '#b91c1c' };
  if (run.status === 'skipped') return { text: `Hoppades över ${when}`, color: '#6b7280' };
  if (run.status === 'running') return { text: 'Pågår', color: '#6b7280' };
  if (run.attempt > 0) return { text: `Försöker igen (försök ${run.attempt} av 5)`, color: '#b45309' };
  return { text: 'Väntar på att starta', color: '#6b7280' };
};

const nextRun = (schedule) => {
  if (schedule.state === 'paused') return 'Pausad';
  if (schedule.state === 'completed') return 'Avslutad';
  return formatWhen(schedule.nextFireAt);
};

export default function ScheduledCard({ companyId }) {
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(getApiUrl(`${companyId}/schedules`), { withCredentials: true });
        if (!cancelled) setSchedules(response?.data?.schedules || []);
      } catch (err) {
        // The overview is optional: a failed read leaves the card out.
        if (!cancelled) setSchedules([]);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  if (schedules.length === 0) return null;

  return (
    <SettingsCard
      icon={CalendarClock}
      title="Schemalagt"
      subtitle="Det här kör LedgerLoop automatiskt åt företaget. Ändra eller stäng av en körning i dess inställningar."
    >
      <table className="table mb-0 align-middle" style={{ fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            <th style={{ fontWeight: 600 }}>Körning</th>
            <th style={{ fontWeight: 600 }}>Nästa körning</th>
            <th style={{ fontWeight: 600 }}>Senaste resultat</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {schedules.map((schedule) => {
            const result = lastResult(schedule.lastRun);
            return (
              <tr key={schedule.key}>
                <td style={{ fontWeight: 600, color: '#374151' }}>{schedule.description || schedule.key}</td>
                <td>{nextRun(schedule)}</td>
                <td style={{ color: result.color }}>
                  {result.text}
                  {schedule.lastRun?.status === 'failed' && schedule.lastRun.reason && (
                    <div style={{ fontSize: '0.75rem' }}>{schedule.lastRun.reason}</div>
                  )}
                </td>
                <td className="text-end">
                  {schedule.settingsSection && (
                    <Link to={`/${companyId}/settings/${schedule.settingsSection}`}>Inställningar</Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SettingsCard>
  );
}
